import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf39cd38-3e4c-4cdf-8fd4-859e0591d26f/artifacts/0ftkiwrk_IMG_2860.png";

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/buying-power", label: "Buying Power" },
  { path: "/how-it-works", label: "How it Works" },
  { path: "/about", label: "About" },
  { path: "/faq", label: "FAQ" },
  { path: "/blog", label: "Blog" },
  { path: "/partners/verify", label: "For Partners" },
];

export default function HeaderDark() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-3 left-3 right-3 md:top-4 md:left-6 md:right-6 z-50 transition-all duration-500 rounded-full ${
        scrolled
          ? "bg-white/35 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
          : "bg-white/70 backdrop-blur-xl border border-white/40 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
      }`}
      data-testid="header-dark"
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-[56px] md:h-[64px] gap-3">
          <Link to="/" data-testid="logo-link-dark" className="flex items-center gap-2 flex-shrink-0">
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden="true"
              className="h-8 md:h-10 w-auto"
            />
            <span
              className="text-lg md:text-xl font-bold tracking-tight"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#2D3748' }}
            >
              finan<span style={{ color: '#FDD552' }}>cify</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-3.5 py-1.5 text-sm font-medium font-manrope rounded-full transition-all ${
                  location.pathname === link.path
                    ? "text-[#52BFB8] bg-[#52BFB8]/10"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-medium text-gray-700 hover:text-[#52BFB8] px-3">
                  Dashboard
                </Link>
                <button
                  onClick={logout}
                  data-testid="nav-logout"
                  className="text-xs text-gray-400 hover:text-gray-700"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3">
                  Sign In
                </Link>
                <Link
                  to="/qualify"
                  data-testid="nav-get-started"
                  className="inline-flex items-center gap-2 bg-[#040914] hover:bg-[#52BFB8] hover:text-[#040914] text-white font-semibold rounded-full px-4 py-2 text-sm transition-all"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile: compact Get Started + hamburger */}
          <div className="flex items-center gap-2 lg:hidden">
            {!user && (
              <Link
                to="/qualify"
                data-testid="mobile-get-started"
                className="inline-flex items-center gap-1.5 bg-[#040914] text-white font-semibold rounded-full px-3.5 py-1.5 text-xs"
              >
                Get started
                <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-gray-900 rounded-full hover:bg-gray-100"
              data-testid="header-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden mt-2 bg-white/95 backdrop-blur-xl border border-gray-200/60 rounded-3xl shadow-[0_16px_40px_rgba(0,0,0,0.15)] overflow-hidden">
          <div className="px-3 py-3 space-y-0.5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`block px-4 py-3 rounded-2xl font-medium font-manrope text-sm ${
                  location.pathname === link.path
                    ? "bg-[#52BFB8]/10 text-[#52BFB8]"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 pt-2 mt-2 flex flex-col gap-1.5 px-1">
              {user ? (
                <>
                  <Link to="/dashboard" className="block px-4 py-2.5 rounded-2xl text-gray-700 font-medium text-sm">
                    Dashboard
                  </Link>
                  <button onClick={logout} className="text-left px-4 py-2.5 text-gray-400 text-sm">
                    Sign Out
                  </button>
                </>
              ) : (
                <Link to="/login" className="block px-4 py-2.5 rounded-2xl text-gray-600 font-medium text-sm">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
